import { ReactNode, createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { ShipmentInActionDetailDto } from 'src/generated/services/TFinancialApi';
import { useToastResult } from 'src/presentations/components/molecules/toast/useToastResult';
import {
  IConfirmShipmentInvoiceArgs,
  IPerformShipmentWorkflowActionArgs,
  useInvoiceSummaryFromParams,
  useIsShipmentAsyncProcessing,
  useConfirmShipmentInvoice,
  usePerformShipmentWorkflowAction,
  invalidateShipmentCaches,
} from 'src/use-cases/invoice/useInvoices';
import { useShipmentSelector } from '../ShipmentSelector';
import { selectBackgroundShipmentResult, clearBackgroundShipmentResult } from 'src/infra/stores/reducer/sseSlice';

export interface IRecalculationToastSelectorProps {
  children: ReactNode;
}

export interface IRecalculationToastValues {
  isLoading: boolean;
  isShowNotification: boolean;
  currentShipmentInvoiceId: string | null;
  affectedShipmentsData: any | null;
  confirmAsyncShipment(props: IConfirmShipmentInvoiceArgs): Promise<void>;
  performWorkflowAction(props: IPerformShipmentWorkflowActionArgs): Promise<void>;
  closeToast(): void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const RecalculationToastDefaultValues = {
  isLoading: false,
  isShowNotification: false,
  currentShipmentInvoiceId: null,
  affectedShipmentsData: null,
  confirmAsyncShipment: async () => {},
  performWorkflowAction: async () => {},
  closeToast: () => {},
};

export const RecalculationToastSelectorContext = createContext<IRecalculationToastValues>(RecalculationToastDefaultValues);

export const RecalculationToastSelector = ({ children }: IRecalculationToastSelectorProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowNotification, setIsShowNotification] = useState<boolean>(false);
  const [currentShipmentInvoiceId, setCurrentShipmentInvoiceId] = useState<string>(null);
  const [affectedShipmentsData, setAffectedShipmentsData] = useState<any>(null);

  const { data: invoiceSummary } = useInvoiceSummaryFromParams();
  const { shipment } = useShipmentSelector();
  const { mutateAsync: confirmShipmentInvoiceMutation } = useConfirmShipmentInvoice();
  const { mutateAsync: performShipmentWorkflowActionMutation } = usePerformShipmentWorkflowAction();
  const { mutateAsync: isShipmentAsyncProcessing } = useIsShipmentAsyncProcessing();
  const { showToastResult } = useToastResult();

  // SSE: Listen for background shipment result from Redux
  const backgroundShipmentResult = useSelector(selectBackgroundShipmentResult);
  const reduxDispatch = useDispatch();
  const queryClient = useQueryClient();

  const showConfirmNotification = useCallback(
    (data: ShipmentInActionDetailDto) => {
      if (data?.backgroundShipmentPerformResultDto?.type === 'SEND_TO_ROLE') {
        const flaggedShipmentInvoices = data?.backgroundShipmentPerformResultDto?.affectedShipmentAndInvoice?.flaggedShipmentInvoices;
        const recalculationData = {
          shipmentId: shipment?.id,
          summary: flaggedShipmentInvoices && flaggedShipmentInvoices.length > 0 ? flaggedShipmentInvoices[0]?.deviationTypeCountMap : null,
          type: data?.backgroundShipmentPerformResultDto?.type,
        };
        showToastResult(
          invoiceSummary?.id,
          flaggedShipmentInvoices[0]?.currentDeviationWorkflow,
          invoiceSummary?.invoiceNumber,
          shipment?.shipmentDetail?.shipmentNumber,
          recalculationData
        );
      } else {
        setIsShowNotification(true);
      }
    },
    [showToastResult, invoiceSummary?.id, invoiceSummary?.invoiceNumber, shipment?.shipmentDetail?.shipmentNumber, shipment?.id]
  );

  // SSE: Process background shipment result when it arrives
  useEffect(() => {
    if (!backgroundShipmentResult || !currentShipmentInvoiceId) return;
    if (backgroundShipmentResult.shipmentInvoiceId !== currentShipmentInvoiceId) return;

    const result = backgroundShipmentResult.result; // This IS backgroundShipmentPerformResultDto
    const affectedData = result?.affectedShipmentAndInvoice;

    setAffectedShipmentsData(affectedData);
    // Wrap result so showConfirmNotification can access it at data.backgroundShipmentPerformResultDto
    showConfirmNotification({ backgroundShipmentPerformResultDto: result } as ShipmentInActionDetailDto);
    // Note: setIsLoading(false) is handled by polling in handleAsyncResponse

    // Invalidate caches (same as what useIsShipmentAsyncProcessing.onSuccess did)
    invalidateShipmentCaches(queryClient, backgroundShipmentResult.invoiceId);

    // Clear the SSE event from Redux
    reduxDispatch(clearBackgroundShipmentResult());
  }, [backgroundShipmentResult, currentShipmentInvoiceId, showConfirmNotification, queryClient, reduxDispatch]);

  const handleAsyncResponse = useCallback(
    async (shipmentInvoiceId: string, response: any) => {
      if (response?.data?.data?.asyncProcessing) {
        // Poll isShipmentAsyncProcessing for loading state (spinner)
        // The toast notification will arrive separately via SSE event
        const checkProcessingStatus = async () => {
          const isProcessing = await isShipmentAsyncProcessing(shipmentInvoiceId);
          if (isProcessing) {
            setTimeout(checkProcessingStatus, 1000);
          } else {
            setIsLoading(false);
          }
        };
        await checkProcessingStatus();
      } else {
        setIsLoading(false);
      }
    },
    [isShipmentAsyncProcessing]
  );

  const confirmAsyncShipment = useCallback(
    async (props: IConfirmShipmentInvoiceArgs) => {
      setIsLoading(true);
      setCurrentShipmentInvoiceId(props.shipmentInvoiceId);
      const response = await confirmShipmentInvoiceMutation(props);
      await handleAsyncResponse(props.shipmentInvoiceId, response);
    },
    [confirmShipmentInvoiceMutation, handleAsyncResponse]
  );

  const performWorkflowAction = useCallback(
    async (props: IPerformShipmentWorkflowActionArgs) => {
      setIsLoading(true);
      setCurrentShipmentInvoiceId(props.shipmentInvoiceId);
      const response = await performShipmentWorkflowActionMutation(props);
      await handleAsyncResponse(props.shipmentInvoiceId, response);
    },
    [performShipmentWorkflowActionMutation, handleAsyncResponse]
  );

  const closeToast = useCallback(() => {
    setIsShowNotification(false);
    setCurrentShipmentInvoiceId(null);
    setAffectedShipmentsData(null);
  }, []);

  const value = useMemo(
    () => ({
      isLoading,
      isShowNotification,
      currentShipmentInvoiceId,
      confirmAsyncShipment,
      performWorkflowAction,
      affectedShipmentsData,
      closeToast,
    }),
    [isLoading, isShowNotification, currentShipmentInvoiceId, affectedShipmentsData, confirmAsyncShipment, performWorkflowAction, closeToast]
  );

  return <RecalculationToastSelectorContext.Provider value={value}>{children}</RecalculationToastSelectorContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useRecalculationToastSelector() {
  const context = useContext(RecalculationToastSelectorContext);

  if (!context) {
    throw new Error(`Recalculation Toast selector compound components cannot be rendered outside the Recalculation Toast Selector component`);
  }
  return context;
}
